/**
 * Confirmation dialog for destructive or high-consequence actions.
 *
 * `consequence` is a REQUIRED prop, not an optional nicety: a confirm dialog
 * that only asks "are you sure?" trains people to click through it. Every use
 * must spell out what changes — "they will lose all access to this team's data",
 * "this user will be able to enter plays and resolve conflicts".
 *
 * Built on `OverlayDialog`, so it inherits the focus trap, Escape handling,
 * backdrop dismissal and focus restore. Errors surface inside the dialog with a
 * retry rather than closing it and losing the user's intent.
 */
import { useState, type ReactNode } from 'react';

import { OverlayDialog } from './overlay-dialog';

export interface ConfirmDialogProps {
  title: string;
  /** What will happen if the user confirms. Rendered prominently. */
  consequence: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` paints the confirm button red — use it for removals. */
  tone?: 'primary' | 'danger';
  /** Extra controls (e.g. a persona picker) shown above the buttons. */
  children?: ReactNode;
  isConfirmDisabled?: boolean;
  /** Human-readable failure from the last attempt. Never a raw stack trace. */
  error?: string | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  consequence,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  children,
  isConfirmDisabled = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const [isBusy, setIsBusy] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await onConfirm();
    } finally {
      // The dialog may already be unmounted on success; React 18 tolerates the
      // no-op, and on failure this re-enables the retry.
      setIsBusy(false);
    }
  };

  return (
    <OverlayDialog title={title} onClose={isBusy ? () => undefined : onCancel}>
      <div className="confirm-dialog">
        <p className="confirm-dialog__consequence">{consequence}</p>
        {children}
        {error !== null ? (
          <div className="banner banner--error" role="alert">
            <p>{error}</p>
          </div>
        ) : null}
        <div className="btn-row confirm-dialog__actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isBusy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${tone === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            onClick={() => void handleConfirm()}
            disabled={isBusy || isConfirmDisabled}
          >
            {isBusy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </OverlayDialog>
  );
}
